import { ArrowTopRightOnSquareIcon, StarIcon } from '@heroicons/react/24/outline';
import { Box, Button } from '@mantine/core';
import { Link, useNavigate } from 'react-router-dom';
import { ROUTES } from '../../../constants/routes';
import { CVDownloadMenu } from '../../../features/CVEditor/components/CVDownloadMenu';
import { Person } from '../../../models/person.model';
import { PersonService } from '../../../services/personService';
import { isPersonCurrentlyUnassigned } from '../../../utils/availabilityUtils';
import { ConnectedPersonActionsMenu } from '../PersonActionsMenu';

interface CVBottomNavigationProps {
  personId: number;
  person: Person;
  fullName: string;
  onClose: () => void;
  onRefresh?: () => void;
  onRefreshData: () => void;
}

export function CVBottomNavigation({ personId, person, fullName, onClose, onRefresh, onRefreshData }: CVBottomNavigationProps) {
  const navigate = useNavigate();

  return (
    <Box p="sm" style={{ flexShrink: 0, borderTop: '1px solid var(--mantine-color-gray-3)' }}>
      {/* Top row: navigation */}
      <Box style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        <Button
          component={Link}
          to={ROUTES.CV_DETAILS(personId)}
          variant="filled"
          color="brand"
          size="sm"
          style={{ flex: 1 }}
          title="Click to view • Ctrl+Click to open in new tab"
        >
          View Full Profile
        </Button>
        <Button
          component="a"
          href={`${window.location.origin}${ROUTES.CV_DETAILS(personId)}`}
          target="_blank"
          rel="noopener noreferrer"
          variant="light"
          color="brand"
          size="sm"
          style={{ flex: 1 }}
          leftSection={<ArrowTopRightOnSquareIcon style={{ width: 13, height: 13 }} />}
          title="Open profile in new tab"
        >
          New Tab
        </Button>
      </Box>

      {/* Bottom row: download, actions, skills */}
      <Box style={{ display: 'flex', gap: 6 }}>
        <Box style={{ flex: 1 }}>
          <CVDownloadMenu
            personId={personId}
            userName={fullName}
            fullWidth
            buttonProps={{ size: 'sm' }}
          />
        </Box>
        <Box style={{ flex: 1 }}>
          <ConnectedPersonActionsMenu
            person={person}
            isUnassigned={isPersonCurrentlyUnassigned(person.availabilityDate)}
            isInactive={person.inactive}
            onModify={(e) => {
              e?.preventDefault();
              e?.stopPropagation();
              navigate(ROUTES.CV_EDITOR_WITH_ID(person.id));
            }}
            onDelete={(e) => {
              e?.preventDefault();
              e?.stopPropagation();
              if (window.confirm(`Are you sure you want to delete ${person.firstName} ${person.lastName}'s CV?`)) {
                const personService = new PersonService();
                personService.deletePerson(person).then(() => {
                  onClose();
                  onRefresh?.();
                });
              }
            }}
            variant="button"
            fullWidth
            size="sm"
            onRefresh={() => {
              onRefreshData();
              onRefresh?.();
            }}
          />
        </Box>
        <Box style={{ flex: 1 }}>
          <Button
            component={Link}
            to={ROUTES.PERSON_SKILLS(personId)}
            variant="light"
            color="brand"
            fullWidth
            size="sm"
            leftSection={<StarIcon style={{ width: 13, height: 13 }} />}
            title="Click to view • Ctrl+Click to open in new tab"
          >
            Skills
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

